import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, Calendar as CalendarIcon, Copy, Loader2, Save, RefreshCw, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import WhatsAppConfigTab from "@/pages/WhatsAppConfigTab";

const SECTIONS = [
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { key: "smtp", label: "E-mail (SMTP)", icon: Mail },
  { key: "calendar", label: "Google/Apple Calendar", icon: CalendarIcon },
];

export default function IntegrationsTab() {
  const [sub, setSub] = useState("whatsapp");
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="integrations-subnav">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const active = sub === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setSub(s.key)}
              data-testid={`integrations-subnav-${s.key}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm border transition-colors ${
                active
                  ? "border-tenant text-tenant-foreground"
                  : "border-border text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
              style={active ? { background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))`, borderColor: `hsl(var(--tenant-primary))` } : {}}
            >
              <Icon className="w-4 h-4" strokeWidth={1.6} />
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="pt-2">
        {sub === "whatsapp" && <WhatsAppConfigTab />}
        {sub === "smtp" && <SMTPCard />}
        {sub === "calendar" && <CalendarCard />}
      </div>
    </div>
  );
}

function SMTPCard() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    host: "", port: 587, username: "", password: "",
    encryption: "tls", from_email: "", from_name: "", enabled: true,
  });
  const [hasPw, setHasPw] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tenant/integrations/smtp");
        setCfg(data);
        setHasPw(!!data.has_password);
        if (data.host) {
          setForm((f) => ({ ...f, ...data, password: "" }));
        }
      } catch {}
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form, port: Number(form.port) };
      if (!payload.password && hasPw) {
        // Need to either keep current password (backend has no merge) — require user to re-enter
        toast.error("Por segurança, digite a senha novamente para salvar.");
        setBusy(false); return;
      }
      await api.put("/tenant/integrations/smtp", payload);
      toast.success("SMTP salvo");
      setHasPw(true);
      setForm({ ...form, password: "" });
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    if (!testTo) { toast.error("Informe um e-mail para o teste"); return; }
    setTesting(true);
    try {
      await api.post("/tenant/integrations/smtp/test", { to: testTo });
      toast.success(`E-mail de teste enviado para ${testTo}`);
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setTesting(false); }
  };

  return (
    <Card className="p-6 rounded-tenant border-border">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg grid place-items-center text-tenant-foreground"
          style={{ background: `hsl(var(--tenant-primary))` }}>
          <Mail className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display text-lg font-semibold">SMTP (envio de e-mail)</div>
          <div className="text-xs text-muted-foreground">
            Use seu Gmail, Outlook ou provedor de hospedagem. Recomendado começar com <strong>Gmail + senha de app</strong>.
          </div>
        </div>
      </div>

      <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label>Servidor SMTP</Label>
          <Input required data-testid="smtp-host" placeholder="smtp.gmail.com" value={form.host}
            onChange={(e) => setForm({ ...form, host: e.target.value })} />
        </div>
        <div className="space-y-1.5"><Label>Porta</Label>
          <Input required type="number" data-testid="smtp-port" value={form.port}
            onChange={(e) => setForm({ ...form, port: e.target.value })} />
        </div>
        <div className="space-y-1.5"><Label>Usuário</Label>
          <Input required data-testid="smtp-user" placeholder="seu-email@gmail.com" value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })} />
        </div>
        <div className="space-y-1.5"><Label>Senha {hasPw && <span className="text-[10px] text-muted-foreground">(salva — preencha para alterar)</span>}</Label>
          <Input type="password" data-testid="smtp-pass" autoComplete="new-password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <div className="space-y-1.5"><Label>Criptografia</Label>
          <Select value={form.encryption} onValueChange={(v) => setForm({ ...form, encryption: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tls">STARTTLS (porta 587)</SelectItem>
              <SelectItem value="ssl">SSL (porta 465)</SelectItem>
              <SelectItem value="none">Nenhuma</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 flex items-end gap-2 pb-1">
          <div className="text-sm">Ativo</div>
          <Switch checked={!!form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
        </div>
        <div className="space-y-1.5"><Label>E-mail remetente</Label>
          <Input required type="email" data-testid="smtp-from-email" placeholder="contato@suaempresa.com.br" value={form.from_email}
            onChange={(e) => setForm({ ...form, from_email: e.target.value })} />
        </div>
        <div className="space-y-1.5"><Label>Nome remetente</Label>
          <Input data-testid="smtp-from-name" placeholder="Demo Salão" value={form.from_name}
            onChange={(e) => setForm({ ...form, from_name: e.target.value })} />
        </div>

        <div className="md:col-span-2 flex justify-end pt-2">
          <Button type="submit" disabled={busy} data-testid="smtp-save" className="gap-2"
            style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar SMTP
          </Button>
        </div>
      </form>

      <div className="mt-6 pt-5 border-t border-border">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Enviar e-mail de teste</div>
        <div className="flex gap-2 max-w-md">
          <Input type="email" placeholder="destinatario@email.com" value={testTo}
            onChange={(e) => setTestTo(e.target.value)} data-testid="smtp-test-to" />
          <Button onClick={test} disabled={testing || !hasPw} data-testid="smtp-test-btn" variant="outline" className="gap-2">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar
          </Button>
        </div>
        {!hasPw && <div className="text-xs text-muted-foreground mt-2">Salve o SMTP antes de testar.</div>}
      </div>
    </Card>
  );
}

function CalendarCard() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/tenant/integrations/calendar");
      setInfo(data);
    })();
  }, []);

  const fullUrl = info ? `${window.location.origin}${info.ics_url}` : "";

  const copy = async () => {
    await navigator.clipboard.writeText(fullUrl);
    toast.success("URL copiada!");
  };

  const rotate = async () => {
    if (!window.confirm("Gerar novo link? O antigo deixará de funcionar.")) return;
    const { data } = await api.post("/tenant/integrations/calendar/rotate");
    setInfo(data);
    toast.success("Novo link gerado");
  };

  return (
    <Card className="p-6 rounded-tenant border-border">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg grid place-items-center text-tenant-foreground"
          style={{ background: `hsl(var(--tenant-primary))` }}>
          <CalendarIcon className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display text-lg font-semibold">Google Agenda / Apple Calendar</div>
          <div className="text-xs text-muted-foreground">
            Adicione esta URL no Google Agenda (<strong>+ → De URL</strong>) — todos os agendamentos aparecem automaticamente.
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Link do feed (privado, mantenha em segredo)</Label>
        <div className="flex gap-2">
          <Input readOnly value={fullUrl} className="font-mono text-xs" data-testid="ics-url" />
          <Button variant="outline" onClick={copy} className="gap-1.5" data-testid="ics-copy"><Copy className="w-4 h-4" /> Copiar</Button>
          <Button variant="outline" onClick={rotate} className="gap-1.5"><RefreshCw className="w-4 h-4" /></Button>
        </div>
        <div className="text-xs text-muted-foreground pt-2">
          <strong>Como usar no Google Agenda:</strong>
          <ol className="list-decimal list-inside mt-1 space-y-0.5">
            <li>Abra <code>calendar.google.com</code></li>
            <li>Na barra lateral, ao lado de "Outras agendas", clique em <strong>+</strong></li>
            <li>Selecione <strong>De URL</strong> e cole o link acima</li>
            <li>Pronto — atualização automática a cada poucos minutos</li>
          </ol>
        </div>
      </div>
    </Card>
  );
}

// Helper used by Agenda to build a per-event "Add to Google" link
export function googleCalendarLink(appt, tenantName = "") {
  if (!appt) return "#";
  const fmt = (iso) => {
    const d = new Date(iso);
    return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  };
  const text = `${appt.service_name} — ${appt.client_name}`;
  const details = [
    `Cliente: ${appt.client_name || ""}`,
    `Serviço: ${appt.service_name || ""}`,
    appt.resource_name ? `Profissional: ${appt.resource_name}` : "",
    appt.notes ? `Notas: ${appt.notes}` : "",
    tenantName ? `Empresa: ${tenantName}` : "",
  ].filter(Boolean).join("\n");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text,
    dates: `${fmt(appt.start_at)}/${fmt(appt.end_at)}`,
    details,
  });
  return `https://www.google.com/calendar/render?${params.toString()}`;
}
